// cards.ts - decode a schema CardIndex into the monochrome-pip view the felt renders, and parse a
// readable card string (e.g. "As") for authoring teaching fixtures. Grounded in the schema's fixed
// encoding (packages/schema/src/primitives.ts): index = rank*4 + suit, rank 0=2..12=A, suit
// 0=c,1=d,2=h,3=s. This is the S19 client-side mirror of coach/cards.ts formatCard, but it keeps rank,
// suit letter, glyph, and red-ness separate so the <Card> pip can be built exactly like the mockup.
import type { CardIndex } from '@postflop/schema';

/** Rank glyphs indexed by rank (0=2 .. 12=A). */
const RANK_CHARS = '23456789TJQKA';
/** Suit letters indexed by suit (0=c,1=d,2=h,3=s). */
const SUIT_CHARS = 'cdhs';
/** Suit glyphs indexed by suit. U+FE0E (variation selector-15) forces TEXT presentation, not emoji,
 *  so the pips stay monochrome (mockup: font-variant-emoji:text + the same variation selector). */
const SUIT_GLYPHS = ['♣︎', '♦︎', '♥︎', '♠︎'];

export interface CardView {
  /** rank glyph, e.g. "A", "T", "7". */
  rank: string;
  /** suit letter, e.g. "s". */
  suit: string;
  /** text-presentation suit glyph. */
  glyph: string;
  /** diamonds + hearts render red. */
  red: boolean;
}

/** CardIndex -> the parts the felt pip needs. */
export function decodeCard(card: CardIndex): CardView {
  const n = card as unknown as number;
  if (!Number.isInteger(n) || n < 0 || n > 51) throw new RangeError(`CardIndex out of range: ${n}`);
  const rankIdx = n >> 2;
  const suitIdx = n & 3;
  return {
    rank: RANK_CHARS[rankIdx],
    suit: SUIT_CHARS[suitIdx],
    glyph: SUIT_GLYPHS[suitIdx],
    red: suitIdx === 1 || suitIdx === 2,
  };
}

/** "As" / "Td" / "7c" -> CardIndex. Authoring convenience for teaching fixtures. */
export function parseCardString(code: string): CardIndex {
  const rankIdx = RANK_CHARS.indexOf(code[0]);
  const suitIdx = SUIT_CHARS.indexOf(code[1]);
  if (code.length !== 2 || rankIdx < 0 || suitIdx < 0) {
    throw new RangeError(`expected a card string like "As" or "Td", got "${code}"`);
  }
  return (rankIdx * 4 + suitIdx) as unknown as CardIndex;
}
